import React from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import DialogContentText from '@mui/material/DialogContentText'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import InputBase from '@mui/material/InputBase'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Skeleton from '@mui/material/Skeleton'
import Snackbar from '@mui/material/Snackbar'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import CheckIcon from '@mui/icons-material/Check'
import ReplayIcon from '@mui/icons-material/Replay'
import makeStyles from '@mui/styles/makeStyles'

import AffiliateTracking from './AffiliateTracking'

import { useHistory, useParams } from 'react-router-dom'
import { useMutation, useQuery } from 'react-query'

import { FirebaseContext } from '../../utils/firebase'
import 'firebase/auth'

import { API_ROOT_URL } from '../../constants'
import * as ROUTES from '../../constants/routes'
import { CLICKBANK } from '../../constants/platforms'

import clickbankLogoImg from '../../media/clickbank_logo.png'

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    left: 66,
    right: 0,
    width: 'calc(100% - 66px)',
    height: '100vh',
    overflowY: 'auto',
    backgroundColor: theme.palette.grey[50],
  },
  logoImg: {
    width: 100,
  },
  syncButton: {
    width: 110,
  },
  redButton: {
    color: theme.palette.error.main,
    '&:hover': {
      background: 'rgba(255,23,68,0.04)',
    },
  },
}))

const REVOKE_SUCCESS_MESSAGE = 'Account removed. Data will no longer be synced from this account. Redirecting back to settings...'
const WEBHOOK_URL_BASE = `https://us-central1-ltv-numbers-${process.env.REACT_APP_ENV === 'production' ? 'prod' : 'dev'}.cloudfunctions.net/webhooks/clickbank/`

const CONFIG_MAP = {
  affiliate_amount_field: {
    display: 'Affiliate Amount Field',
    type: 'select',
    default: 'account_amount',
    options: [
      {
        display: 'Account Amount',
        value: 'account_amount'
      },
      {
        display: 'JV Payout Amount',
        value: 'jv_payout_amount'
      },
      {
        display: 'Account + JV Payout Amount',
        value: 'account_plus_jv_payout_amount'
      },
    ],
  },
  ignore_seller_ids: {
    display: 'Ignore Seller IDs',
    type: 'text',
    default: '',
    placeholder: 'seller1,seller2,seller3',
  },
}

const ClickbankSource = () => {
  const classes = useStyles()
  const { id } = useParams()
  const history = useHistory()
  const firebase = React.useContext(FirebaseContext)

  const [webhookUrl, setWebhookUrl] = React.useState('')

  const [message, setMessage] = React.useState('')
  const [showMessage, setShowMessage] = React.useState(false)
  const [messageSeverity, setMessageSeverity] = React.useState('success')
  const [showRevokeConfirmation, setShowRevokeConfirmation] = React.useState(false)


  const [config, setConfig] = React.useState(() => {
    const initialConfig = {}
    Object.keys(CONFIG_MAP).forEach(key => {
      initialConfig[key] = CONFIG_MAP[key].default
    })
    return initialConfig
  })

  // Maintain config local state for text fields to handle unsaved changes
  const textFieldKeys = Object.keys(CONFIG_MAP).filter(key => CONFIG_MAP[key].type === 'text')
  const [localTextValues, setLocalTextValues] = React.useState(() =>
    Object.fromEntries(textFieldKeys.map(key => [key, config[key] || '']))
  )
  const [dirtyFields, setDirtyFields] = React.useState(() =>
    Object.fromEntries(textFieldKeys.map(key => [key, false]))
  )

  // Keep local state in sync with config changes
  React.useEffect(() => {
    // eslint-disable-next-line no-unused-vars
    setLocalTextValues(prev =>
      Object.fromEntries(textFieldKeys.map(key => [key, config[key] || '']))
    )
    // eslint-disable-next-line no-unused-vars
    setDirtyFields(prev =>
      Object.fromEntries(textFieldKeys.map(key => [key, false]))
    )
  }, [config])

  React.useEffect(() => {
    const uid = firebase.auth().currentUser.uid
    setWebhookUrl(WEBHOOK_URL_BASE + uid + '/integration/' + id)
  }, [])

  const {
    isLoading: isLoadingIntegration,
    data: integration = {}
  } = useQuery(['integration-get', id], () =>
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/integrations/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
      }).then(res => res.json())
    }),
    {
      cacheTime: 15 * 60 * 1000,  // 15 minutes
      staleTime: 15 * 60 * 1000,  // 15 minutes
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data.config) {
          const integrationConfig = JSON.parse(data.config)
          if (Object.keys(integrationConfig).length > 0) {
            setConfig({...config, ...integrationConfig})
          }
        }
      }
    }
  )

  const { isLoading: isLoadingRevoke, refetch: revoke } = useQuery(['revoke-clickbank'], () =>
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/integrations/clickbank/revoke`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          integration_id: id
        })
      }).then(res => res.json())
    }),
    {
      enabled: false,
      staleTime: Infinity,
      refetchOnReconnect: false,
      onSuccess: () => {
        setShowRevokeConfirmation(false)
        setMessage(REVOKE_SUCCESS_MESSAGE)
        setMessageSeverity('success')
        setShowMessage(true)
        setTimeout(() => history.push(ROUTES.SETTINGS), 3000)
      },
    }
  )

  const { isLoading: isUpdatingConfig, mutate: updateConfig } = useMutation(
    ({ key, value }) => firebase.auth().currentUser.getIdToken(false).then(token => {
      if (!key || !value) {
        throw new Error('Key and value must be provided for config update')
      }
      if (!CONFIG_MAP[key]) {
        throw new Error(`Invalid config key: ${key}`)
      }
      return fetch(`${API_ROOT_URL}/api_fs/integrations/${id}/config`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          key,
          value,
        }),
      }).then(res => res.json())
    }),
    {
      onMutate: ({ key, value }) => {
        // Optimistically update the config state
        const previousValue = config[key]
        setConfig(prevConfig => ({
          ...prevConfig,
          [key]: value
        }))
        return { previousValue }
      },
      onSuccess: () => {
        setShowMessage(true)
        setMessage('Data Source successfully updated')
        setMessageSeverity('success')
      },
      onError: (error, { key }, context) => {
        console.error(`Error updating config for key ${key}:`, error)
        const previousValue = context.previousValue
        // Revert the optimistic update if the mutation fails
        if (!previousValue) {
          console.error(`No previous value found for key ${key}. Unable to revert optimistic update.`)
          return
        }
        // Revert the optimistic update
         setConfig(prevConfig => ({
          ...prevConfig,
          [key]: previousValue
        }))
        setShowMessage(true)
        setMessage('Error updating settings. Please try again, and if problem persists, contact support.')
        setMessageSeverity('error')
      }
    }
  )

  const {
    isLoading: isLoadingConnectors,
    data: connectors = [],
  } = useQuery(['connectors-get', id], () =>
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/connectors`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
      }).then(res => res.json())
    }),
    {
      cacheTime: 0,
      staleTime: 0,
      refetchOnWindowFocus: false,
    }
  )

  const handleCloseMessage = (_, reason) => {
    if (reason === 'clickaway') return
    setShowMessage(false)
  }

  const copyToClipboard = (text) => {
    setMessage('Copied to clipboard')
    setShowMessage(true)
    navigator.clipboard.writeText(text)
  }

  return (
    <div className={classes.root}>
      <Box padding={2}>
        <Button
          variant='outlined'
          onClick={() => history.push(ROUTES.SETTINGS)}
          size='small'
        >
          Back
        </Button>
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2} alignItems='center'>
                <Grid item xs={2}>
                  <img
                    className={classes.logoImg}
                    alt='clickbank_logo'
                    src={clickbankLogoImg}
                  />
                </Grid>
                <Grid item xs={10}>
                  <Typography variant='h5' gutterBottom>
                    ClickBank Source
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>
                    Name
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Box height='100%' display='flex' alignItems='center'>
                    <Typography variant='body2'>
                      {isLoadingIntegration ? <Skeleton variant='text' width={100} /> : `${integration.nickname}`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>
                    Seller Tracking
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Box display='block'>
                    <Typography variant='body2' paragraph>
                      To track sales from ClickBank where your account is acting as the Seller, or &quot;Vendor&quot; as ClickBank calls it, add the URL below to your ClickBank account under Vendor Settings &gt; My Site &gt; Advanced Tools &gt; Instant Notification URL.
                      Be sure to select the latest version in the dropdown, and click TEST IPN to verify the connection. Finally, click Save Changes
                      to complete setup. This enables LTV Numbers to track all transactions from ClickBank where you are acting as the Seller.
                    </Typography>
                    <Typography variant='body1' fontWeight='bold'>
                      Instant Notification URL
                    </Typography>
                  </Box>
                  <Box display='flex' alignItems='center'>
                    {isLoadingIntegration ? <Skeleton variant='text' width={100} /> : (
                      <Box display='flex' flexDirection='row' width='100%'>
                        <InputBase
                          variant='filled'
                          value={webhookUrl}
                          size='small'
                          multiline
                          fullWidth
                          disabled
                          sx={{
                            wordBreak: 'break-all',
                            overflowWrap: 'break-word'
                          }}
                        />
                        <Button
                          variant='text'
                          color='primary'
                          onClick={() => copyToClipboard(webhookUrl)}
                        >
                          Copy
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        <AffiliateTracking
          platform={CLICKBANK}
          uid={firebase.auth().currentUser.uid}
          integration_id={id}
          webhookUrlBase={WEBHOOK_URL_BASE}
          connectorUrlSuffix={'ins'}
          urlLabel={'Instant Notification URL'}
          isLoadingConnectors={isLoadingConnectors}
          connectors={connectors}
          setMessage={setMessage}
          setShowMessage={setShowMessage}
          copyToClipboard={copyToClipboard}
          instructionsNode={
            <Typography variant='body2' paragraph>
              <b>IMPORTANT: You will need to add an INS slot to your ClickBank account for each Connector</b>.
              Follow the same instructions as above to add INS Slots, but use the Contact Connector Instant Notification URLs below.
              This ensures that LTV Numbers will be able to match up affiliate sales with the contact that made the purchase.
            </Typography>
          }
        />

        {/* Config */}
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>
                    Settings
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Typography variant='body2' paragraph>
                    Use the configuration options below to customize how data is processed from Clickbank into LTV Numbers.
                  </Typography>
                  <Typography variant='h6' paragraph>
                    Affiliate Amount Field
                  </Typography>
                  <Typography variant='body2' paragraph>
                    <b>NOTE: This setting only applies to Affiliate Tracking</b>
                  </Typography>
                  <Typography variant='body2' paragraph>
                    Select the field that will be used to set the amount earned as an affiliate on a sale. In most cases, the default value of <b>Account Amount</b> will be appropriate,
                    which represents the amount the this account earned on the sale as an affiliate. However, if you are working with a list management company, or a JV Partner,
                    then you may need to modify this value.
                  </Typography>
                  <Typography variant='body2' paragraph>
                    If you are paid through a JV Contract rather than directly from the affiliate account earnings, then you can select <b>JV Payout</b>.
                  </Typography>
                  <Typography variant='body2' paragraph>
                    If your earnings are a combination of both the affiliate Account Amount and the JV Payout, then you can select <b>Account + JV Payout</b>.
                  </Typography>
                  <Typography variant='body2' paragraph>
                    If you are unsure which value to select, please contact the LTV Numbers team for guidance.
                  </Typography>


                  <Typography variant='h6' paragraph>
                    Ignore Seller IDs
                  </Typography>
                  <Typography variant='body2' paragraph>
                    <b>NOTE: This setting only applies to Affiliate Tracking</b>
                  </Typography>
                  <Typography variant='body2' paragraph>
                    If this account is being used for affiliate tracking, then it is important to ensure that sales of your own products are not counted as affiliate earnings.
                  </Typography>
                  <Typography variant='body2' paragraph>
                    In certain scenarios, such as when working with a list management company using a dedicated affiliate account, an external affiliate ID
                    may be used when promoting your own products.
                  </Typography>
                  <Typography variant='body2' paragraph>
                    In this case, you likely have connected your Clickbank seller account to LTV Numbers to track your internal sales. To prevent duplication,
                    we need to ignore affiliate earnings associated with sales of your own products.
                  </Typography>
                  <Typography variant='body2' paragraph>
                    To do this, we can specify a list of Seller IDs to ignore. These are the &quot;nicknames&quot; of the Clickbank accounts whose
                    affiliate sales should be ignored to prevent duplication with your internal sales. Please enter the Seller IDs below, separated by commas.
                    Each value should be lowercase and must exactly match a real Clickbank nickname to work as intended.
                  </Typography>

                  {isLoadingIntegration ? <Skeleton variant='rect' width={300} /> : (
                    <Box marginTop={2} display='flex' alignItems='flex-start'>

                      {Object.keys(CONFIG_MAP).map(key => {
                        const configItem = CONFIG_MAP[key]
                        if (configItem.type === 'select') {
                          return (
                            <FormControl
                              variant='outlined'
                              size='small'
                              margin='dense'
                              sx={{ margin: 1, minWidth: 200 }}
                              key={key}
                              required
                            >
                              <InputLabel id={`${key}-label`}>{configItem.display}</InputLabel>
                              <Select
                                labelId={`${key}-label`}
                                id={`${key}-select`}
                                value={config[key]}
                                onChange={event => updateConfig({ key, value: event.target.value })}
                                label={configItem.display}
                              >
                                {configItem.options.map(option =>
                                  <MenuItem key={option.value} value={option.value}>{option.display}</MenuItem>
                                )}
                              </Select>
                            </FormControl>
                          )
                        } else if (configItem.type === 'text') {
                          return (
                            <TextField
                              key={key}
                              label={configItem.display}
                              name={key}
                              value={localTextValues[key] || ''}
                              onChange={event => {
                                const value = event.target.value
                                setLocalTextValues(prev => ({ ...prev, [key]: value }))
                                setDirtyFields(prev => ({ ...prev, [key]: value !== (config[key] || '') }))
                              }}
                              placeholder={configItem.placeholder}
                              helperText={dirtyFields[key] ? 'Unsaved changes' : ''}
                              variant='outlined'
                              size='small'
                              sx={{ margin: 1, minWidth: 200 }}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position='end'>
                                    {dirtyFields[key] && (
                                      <>
                                        <IconButton
                                          color='inherit'
                                          onClick={() => {
                                            setLocalTextValues(prev => ({ ...prev, [key]: config[key] || '' }))
                                            setDirtyFields(prev => ({ ...prev, [key]: false }))
                                          }}
                                          aria-label='revert'
                                          size='small'
                                          sx={{
                                            width: 28,
                                            height: 28,
                                            marginRight: 0.5,
                                          }}
                                        >
                                          <ReplayIcon fontSize='small' />
                                        </IconButton>
                                        <Button
                                          color='primary'
                                          variant='contained'
                                          disabled={!dirtyFields[key]}
                                          onClick={() => {
                                            updateConfig({ key, value: localTextValues[key] })
                                            setDirtyFields(prev => ({ ...prev, [key]: false }))
                                          }}
                                          edge='end'
                                          aria-label='save'
                                          sx={{
                                            minWidth: 0,
                                            width: 28,
                                            height: 28,
                                            padding: 0,
                                          }}
                                        >
                                          <CheckIcon fontSize='small' />
                                        </Button>
                                      </>
                                    )}
                                  </InputAdornment>
                                )
                              }}
                            />
                          )
                        }
                        return null
                      })}

                      {isUpdatingConfig && (
                        <CircularProgress
                          className={classes.buttonProgress}
                          color='inherit'
                          size={24}
                          sx={{ marginLeft: 2 }}
                        />
                      )}
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>
                    Delete
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Typography variant='body2' gutterBottom>
                    If you want to remove this data integration so transacations can no longer be saved, click the button below.
                  </Typography>
                  <Button
                    className={classes.redButton}
                    variant='text'
                    onClick={() => setShowRevokeConfirmation(true)}
                  >
                    Delete Source
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>
      </Box>
      <Dialog
        open={showRevokeConfirmation}
        onClose={() => setShowRevokeConfirmation(false)}
      >
        <DialogTitle>
          Remove access to account <b>&quot;{integration.nickname}&quot;</b>?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will prevent data from being synced to LTV Numbers.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            className={classes.redButton}
            variant='text'
            onClick={() => setShowRevokeConfirmation(false)}
          >
            Cancel
          </Button>
          <Button
            color='primary'
            onClick={() => revoke()}
          >
            {isLoadingRevoke ? <CircularProgress color='inherit' size={24} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showMessage}
        autoHideDuration={5000}
        onClose={handleCloseMessage}
      >
        <Alert
          severity={messageSeverity}
          variant='filled'
          onClose={handleCloseMessage}
        >
          {message}
        </Alert>
      </Snackbar>
    </div>
  )
}

ClickbankSource.propTypes = {}

export default ClickbankSource